import { unwrapResult } from '@reduxjs/toolkit';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { ExternalLinkIcon } from '@rsos/assets/icons';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { launchAgent511Application } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { getAgent511Width, getAgent511Height } from '@rsos/utils/metaTags';
import openRequestedPopup from '@rsos/utils/openRequestedPopup';
import {
  getInitialPhoneValue,
  sanitizedPhoneNumber,
} from '@rsos/utils/phoneNumberUtils';
import { notifyError } from '@rsos/utils/toastUtils';
import useIsIRP from '@rsos/utils/useIsIRP';
import * as validator from '@rsos/utils/validators';
import { getMostRecentLoc } from '../../utils/getMostRecentLoc';
import { Container, Title } from './TextTo911Form.styles';
import { trackTextTo911FormClick } from './TextTo911Trackings';

const INVALID_PHONE_NUMBER_MSG =
  'This is an invalid number. Please use a valid 10 digit number.';
const API_ERROR_MSG = 'Agent511 failed to launch. Please try again.';

const formValidationSchema = Yup.object().shape({
  phone_number: Yup.string().test(
    'isValidPhone',
    INVALID_PHONE_NUMBER_MSG,
    function (value) {
      const { path, createError } = this;
      const result = validator.isValidPhone(value)
        ? true
        : createError({ path, message: INVALID_PHONE_NUMBER_MSG });
      return result;
    },
  ),
});

const screenWidth = screen.width;
const screenHeight = screen.height;
const windowFeatures = `width=${getAgent511Width()},height=${getAgent511Height()},left=${
  screenWidth - getAgent511Width()
},top=${screenHeight - getAgent511Height()}`;

const TextTo911Form = ({ onClose }) => {
  const dispatch = useDispatch();

  const { irpVersion } = useIsIRP();

  const { name: orgName } = useSelector(state => state.sinatra.user.currentOrg);

  const isLoading = useSelector(
    state => state.emergencyCalls.loading?.launchAgent511Application,
  );

  const { callQueue } = useSelector(state => state.emergencyCalls);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const hasCallLocation = getMostRecentLoc(callQueue[selectedCallerID]);

  const initialValues = {
    phone_number: getInitialPhoneValue(hasCallLocation ? selectedCallerID : ''),
  };

  const handleSubmit = values => {
    const phoneNumber = sanitizedPhoneNumber(values.phone_number);
    // eslint-disable-next-line no-prototype-builtins
    const doesCallExistInQueue = callQueue.hasOwnProperty(phoneNumber);
    trackTextTo911FormClick(irpVersion, doesCallExistInQueue);
    dispatch(launchAgent511Application({ orgName, phoneNumber }))
      .then(unwrapResult)
      .then(response => {
        if (response?.url) {
          openRequestedPopup(response.url, '_blank', windowFeatures);
        }
        onClose();
      })
      .catch(error => {
        const errorMessage = error?.data?.detail
          ? error.data.detail
          : API_ERROR_MSG;
        notifyError(errorMessage);
        onClose();
      });
  };

  return (
    <Container>
      <Title>Launch Chat</Title>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={values => handleSubmit(values)}
        validationSchema={formValidationSchema}
        validateOnMount={true}
      >
        {({
          errors,
          handleBlur,
          isValid,
          setFieldValue,
          setFieldTouched,
          values,
        }) => (
          <Form id="text-to-911-form">
            <Box marginBottom={32}>
              <InputText
                inputType="tel"
                id="phone_number"
                country="US"
                countries={['US']}
                label="Phone Number"
                placeholder="Enter a ten-digit phone number"
                value={values.phone_number}
                setFieldValue={setFieldValue}
                shouldErrorTakeSpace
                onBlur={phoneNumber => {
                  handleBlur('phone_number', phoneNumber);
                  setFieldTouched('phone_number', phoneNumber);
                }}
                onChange={phoneNumber => {
                  setFieldValue('phone_number', phoneNumber);
                }}
                error={
                  errors.phone_number && !!values.phone_number
                    ? errors.phone_number
                    : null
                }
              />
            </Box>
            <Button
              type="submit"
              form="text-to-911-form"
              id="text-to-911-launch-button"
              data-name="text-to-911-launch-button"
              disabled={!isValid}
              isLoadingProp={isLoading}
              grow
              noMargin
            >
              <Box horizontal align="center">
                <Text size="normal" mr="4px">
                  Launch
                </Text>
                <ExternalLinkIcon width={12} height={12} />
              </Box>
            </Button>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

TextTo911Form.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default TextTo911Form;
